import { Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

@Directive({
  selector: 'img[appAuthSrc]',
  standalone: true
})
export class AuthImageDirective implements OnChanges, OnDestroy {
  @Input() appAuthSrc?: string | null;
  @Input() fallback?: string | null;
  /**
   * BUG-014 (regressivo-pos-migracao 2026-07-29): emite quando a request de
   * avatar falha (404 do backend, timeout, erro de rede). Permite que templates
   * troquem para fallback textual (ex.: iniciais) em vez de imagem placeholder.
   * Nao emite quando `appAuthSrc` esta vazio (nem tentamos carregar).
   */
  @Output() loadError = new EventEmitter<void>();

  private objectUrl: string | null = null;
  private requestSub: Subscription | null = null;

  constructor(
    private el: ElementRef<HTMLImageElement>,
    private http: HttpClient
  ) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.loadImage();
  }

  ngOnDestroy(): void {
    this.clearRequest();
    this.revokeObjectUrl();
  }

  private loadImage(): void {
    this.clearRequest();
    this.revokeObjectUrl();
    const src = this.appAuthSrc?.trim();

    if (!src) {
      this.setSrc(this.fallback);
      return;
    }

    if (this.isDirectSource(src)) {
      this.setSrc(src);
      return;
    }

    const requestUrl = this.normalizeRequestUrl(src);

    this.requestSub = this.http.get(requestUrl, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        this.objectUrl = URL.createObjectURL(blob);
        this.setSrc(this.objectUrl);
      },
      error: () => {
        this.revokeObjectUrl();
        this.setSrc(this.fallback);
        // BUG-014: sinaliza pro template — cards do organograma trocam pra iniciais.
        this.loadError.emit();
      }
    });
  }

  private isDirectSource(src: string): boolean {
    if (src.startsWith('blob:') || src.startsWith('data:')) return true;
    return !this.requiresAuthFetch(src);
  }

  private requiresAuthFetch(src: string): boolean {
    if (src.startsWith('http://') || src.startsWith('https://')) return true;
    return src.startsWith('/api/') || src.includes('/api/');
  }

  private normalizeRequestUrl(src: string): string {
    // Quando recebemos um path relativo de API (ex.: "/api/colaborador/2/avatar"),
    // garantimos que a chamada vá para o backend (environment.apiBaseUrl),
    // e não para o host do frontend (localhost:4200).
    if (src.startsWith('/api/')) {
      const base = environment.apiBaseUrl?.replace(/\/+$/, '') ?? '';
      return `${base}${src}`;
    }
    return src;
  }

  private setSrc(value?: string | null): void {
    const element = this.el.nativeElement;
    if (value) {
      element.src = value;
    } else {
      element.removeAttribute('src');
    }
  }

  private clearRequest(): void {
    if (this.requestSub) {
      this.requestSub.unsubscribe();
      this.requestSub = null;
    }
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
